/**
 * ChooseWorkspacePage.js — RC 1.30.B0 workspace chooser.
 *
 * Shown to users who have >1 allowed workspace and no valid stored
 * default. Clicking a tile lands them in the workspace AND persists
 * the choice as their new default (via `switchWorkspace`).
 */
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Briefcase, Building2, Shield } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { useWorkspace, landingPathFor } from '@/contexts/WorkspaceContext';

const TILES = {
  candidate: {
    label: 'Candidate',
    description: 'Jobs, applications, documents, workflow.',
    Icon: Briefcase,
  },
  employer: {
    label: 'Employer',
    description: 'Roles and matches (coming in RC 1.30.B).',
    Icon: Building2,
  },
  admin: {
    label: 'Admin',
    description: 'Jobs inventory, sources, catalogue.',
    Icon: Shield,
  },
};

export default function ChooseWorkspacePage() {
  const { allowedWorkspaces, effectiveDefault, switchWorkspace, loading } = useWorkspace();
  const navigate = useNavigate();

  // Single-workspace users should never see this page — route them
  // straight to their landing path.
  useEffect(() => {
    if (loading) return;
    if (allowedWorkspaces.length === 1) {
      navigate(landingPathFor(allowedWorkspaces[0]), { replace: true });
    }
  }, [loading, allowedWorkspaces, navigate]);

  if (loading) return null;

  return (
    <div
      className="mx-auto max-w-3xl py-16 px-4"
      data-testid="choose-workspace-page"
    >
      <div className="text-center mb-8">
        <h1 className="text-2xl font-semibold text-ndor-ink">
          Choose your workspace
        </h1>
        <p className="mt-2 text-sm text-ndor-ink-muted">
          You have access to more than one. Pick where you'd like to start —
          we'll remember it for next time.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {allowedWorkspaces.map((ws) => {
          const tile = TILES[ws];
          if (!tile) return null;
          const { Icon } = tile;
          const isDefault = effectiveDefault === ws;
          return (
            <button
              key={ws}
              type="button"
              onClick={() => switchWorkspace(ws)}
              className="text-left"
              data-testid={`choose-workspace-tile-${ws}`}
            >
              <Card className="border-ndor-edge hover:border-ndor-gold transition-colors">
                <CardContent className="py-6 flex items-start gap-4">
                  <Icon className="h-6 w-6 text-ndor-ink-muted shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-ndor-ink">{tile.label}</span>
                      {isDefault && (
                        <span className="text-[10px] uppercase tracking-wider text-ndor-gold font-mono-ui">
                          current default
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-ndor-ink-muted mt-1">
                      {tile.description}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </button>
          );
        })}
      </div>
    </div>
  );
}
